import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'

import { useTranslation } from 'react-i18next'
import Modal from '../../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { combineYearMonthAndDay, format, getYear, nowDate, prevMonth } from '../../../../utils/date'
import { useParams } from 'react-router-dom'
import { LangValue } from '../../../../states/types/common'
import { createDataSheetExport, fetchDataSheetExportList } from '../../../../states/containers/export/exportSlice'
import { getZendeskToken } from '../../../../states/apis/auth'
import { setErrors } from '../../../../states/containers/common/errorsSlice'
import { openNewTab } from '../../../../utils/url'

interface ExportModalState {
  display: boolean,
  type: string,
  id: number | undefined,
}

interface Props {
  exportModalState: ExportModalState
  closeCallback: VoidFunction
}

const ExportModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const {
    exportModalState,
    closeCallback,
  } = props

  const initialInput = {
    fileType: "office_hours",
    targetYear: Number(`${format(prevMonth(nowDate()), "YYYY")}`),
    targetDate: `${format(prevMonth(nowDate()), "YYYYMM", language)}`,
  }

  const [inputFileType, setInputFileType] = useState<string>(initialInput.fileType)

  const [targetYear, setTargetYear] = useState<number>(initialInput.targetYear)
  const [targetDate, setTargetDate] = useState<string>(initialInput.targetDate)

  const [displayDate, setDisplayDate] = useState<string>(
    combineYearMonthAndDay(
      `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
    ),
  )

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [displayModal, setDisplayModal] = useState<boolean>(false)

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const openCalendarModal = () => {
    setIsCalendarDisplay(true)
  }

  const closeCalendarModal = () => {
    setIsCalendarDisplay(false)
  }

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    setTargetDate(`${String(targetYear)}${value.slice(-2)}`)
    setDisplayDate(
      combineYearMonthAndDay(`${String(targetYear)}${value.slice(-2)}`, 1, t('time.date.formats.month_year'), language),
    )
  }

  const updateFileType = (value: string) => {
    setInputFileType(value)
  }

  const saveExport = () => {
    if (!commonPart.company?.id) return

    dispatch(
      createDataSheetExport(
        {
          target: targetDate,
          file_type: inputFileType,
        },
        () => {
          alert.success(
            <>
              {t('export.alert.start')}
            </>,
          )
          dispatch(fetchDataSheetExportList())
          closeCallback()
        },
      ),
    )
  }

  const clickZendeskLink = (url: string) => {
    getZendeskToken()
      .then((result) => {
        openNewTab(`${result.url}&return_to=${url}`, 'post')
      })
      .catch((error) => {
        dispatch(setErrors(error))
      })
  }

  useEffect(() => {
    if (!exportModalState) return
    setInputFileType(initialInput.fileType)
    setTargetYear(initialInput.targetYear)
    setTargetDate(initialInput.targetDate)
    setDisplayDate(
      combineYearMonthAndDay(
        `${String(initialInput.targetYear)}${(`0${prevMonth(nowDate(), 'MM')}`).slice(-2)}`, 1, t('time.date.formats.month_year'), language,
      ),
    )
  }, [exportModalState])

  useEffect(() => {
    setDisplayModal(exportModalState.display)
    setIsCalendarDisplay(false)
  }, [exportModalState.display])

  return (
    <Modal
      title={t('export.data.title')}
      show={displayModal}
      wrapperClass="export_data"
      closeCallback={closeCallback}
      closeable
    >
      {
        commonPart.payment_finished ? (
          <>
            <div className="item">
              <div className="title">
                {t('export.select_type.title')}
              </div>
              <div className="detail row_1">
                <select value={inputFileType} onChange={(e) => updateFileType(e.target.value)}>
                  <option value="office_hours">{t('export.select_type.office_hours')}</option>
                  <option value="application_analysis">{t('export.select_type.application_analysis')}</option>
                  <option value="time_trend">{t('export.select_type.time_trend')}</option>
                  <option value="percent_of_time_by_category">{t('export.select_type.percent_of_time_by_category')}</option>
                  <option value="number_of_applications">{t('export.select_type.number_of_applications')}</option>
                </select>
                <div className="text_link">
                  {t('export.data.explanation')}
                  <span
                    role="none"
                    onClick={() => clickZendeskLink("https://qasee.zendesk.com/hc/ja/articles/4407437209625")}
                  >
                    {t('export.data.here')}
                  </span>
                </div>
              </div>
            </div>
            <div className="item">
              <div className="title">
                {t('export.select_month.title')}
              </div>
              <div className="detail row_1">
                <div className="calendar_text">
                  <button
                    type="button"
                    className="display button in_modal"
                    onClick={() => openCalendarModal()}
                  >
                    {displayDate}
                    <FontAwesomeIcon icon={['fas', 'angle-down']} className={`in_modal ${isCalendarDisplay && 'open'}`} />
                  </button>
                </div>
                <div className={`calendar_box in_modal ${isCalendarDisplay || 'none'}`}>
                  <div className="range">
                    <div className="text">
                      {t(`range.calendar.header_text.monthly`)}
                    </div>
                  </div>
                  <div className="monthly">
                    <div className="select_month">
                      <div>
                        <button
                          type="button"
                          onClick={() => setTargetYear(targetYear - 1)}
                          disabled={Number(format(commonPart.company?.created_at, 'YYYY')) >= targetYear}
                        >
                          <FontAwesomeIcon icon={['fas', 'angle-left']} className="in_modal" />
                        </button>
                      </div>
                      <div>
                        {`${targetYear}${t('time.time.short.year')}`}
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => setTargetYear(targetYear + 1)}
                          disabled={getYear() <= targetYear}
                        >
                          <FontAwesomeIcon icon={['fas', 'angle-right']} className="in_modal" />
                        </button>
                      </div>
                    </div>
                    {
                      month.map((v, i) => {
                        const target = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
                        const isClickableMonth = Number(nowDate('YYYYMM')) >= Number(target)
                          && Number(format(commonPart.company?.created_at, 'YYYYMM')) <= Number(target)
                        const isSelected = targetDate === target
                        return (
                          <div className="monthly_button" key={v}>
                            <button
                              type="button"
                              className={isSelected ? "selected" : ""}
                              onClick={() => handleChangeMonth(target)}
                              disabled={!isClickableMonth}
                            >
                              {`${v}${t('time.time.short.month')}`}
                            </button>
                          </div>
                        )
                      })
                    }
                  </div>
                </div>
                <div className={`screen in_modal transparent ${isCalendarDisplay || 'none'}`} role="presentation" onClick={closeCalendarModal} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              トライアル期間中はご利用になれません
            </div>
            <div
              style={{
                marginTop: "16px",
                textAlign: "center",
              }}
            >
              出力できるデータに関する情報は、
              <span
                role="none"
                onClick={() => clickZendeskLink("https://qasee.zendesk.com/hc/ja/articles/4407437209625")}
                style={{
                  color: "#337ab7",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                こちら
              </span>
              をご参照ください。
            </div>
          </>
        )
      }
      {
        commonPart.payment_finished ? (
          <>
            <button type="button" className="button cancel" onClick={closeCallback}>
              {t('setting.personal.custom_alert.modal.button.cancel')}
            </button>
            <button
              type="button"
              className="button"
              onClick={saveExport}
            >
              {t('export.button.submit')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="button cancel" onClick={closeCallback}>
              閉じる
            </button>
          </>
        )
      }
    </Modal>
  )
}

export default ExportModal
